import React, { forwardRef, useContext } from 'react';
import classNames from 'classnames';

import {
  AppContainerContext,
  Comp,
  Flexbox,
  FocusWithinGroupContext,
  ListboxContext,
  getContainerProps,
  isComponent,
  partitionComponents,
  useTheme,
  warnOnUnknownChildren,
} from '@redsift/design-system';
import { ComboboxContentProps } from './types';
import {
  FloatingPortal,
  PopoverContent,
  usePopoverContext,
  StyledPopoverContent,
  useMergeRefs,
} from '@redsift/popovers';
import { ComboboxContentHeader } from '../combobox-content-header';
import { ComboboxContentFooter } from '../combobox-content-footer';
import { ComboboxContentListbox } from '../combobox-content-listbox/ComboboxContentListbox';
import { ComboboxContext } from '../combobox/context';

const COMPONENT_NAME = 'ComboboxContent';
const CLASSNAME = 'redsift-combobox-content';
const ALLOWED_CHILDREN = new Set(['ComboboxContentHeader', 'ComboboxContentListbox', 'ComboboxContentFooter']);

/**
 * The ComboboxContent component.
 */
export const BaseComboboxContent: Comp<ComboboxContentProps, HTMLDivElement> = forwardRef((props, ref) => {
  const { children, className, style } = props;
  const containerProps = getContainerProps(props);

  const { getFloatingProps, isOpen, refs, strategy, x, y } = usePopoverContext();
  const popoverRef = useMergeRefs([refs.setFloating, ref]);

  const appContainerState = useContext(AppContainerContext);
  const comboboxState = useContext(ComboboxContext);
  const focusContext = useContext(FocusWithinGroupContext);
  const listboxState = useContext(ListboxContext);
  const theme = useTheme();

  if (process.env.NODE_ENV !== 'production') {
    warnOnUnknownChildren(children, 'ComboboxContent', ALLOWED_CHILDREN);
  }

  const [[header], [listbox], [footer]] = partitionComponents(React.Children.toArray(children), [
    isComponent('ComboboxContentHeader'),
    isComponent('ComboboxContentListbox'),
    isComponent('ComboboxContentFooter'),
  ]);

  return (
    <FloatingPortal root={appContainerState?.appContainerRef.current}>
      {isOpen && (
        <StyledPopoverContent
          $theme={theme!}
          ref={popoverRef}
          {...getFloatingProps(props)}
          style={{
            position: strategy,
            top: y ?? 0,
            left: x ?? 0,
            ...style,
            ...(comboboxState?.inputValue?.length && focusContext.state.tabStops.length === 0
              ? { display: 'none' }
              : {}),
          }}
          className={classNames(PopoverContent.className, BaseComboboxContent.className, className)}
          aria-disabled={listboxState.state.isDisabled}
        >
          <Flexbox flexDirection="column" gap="0px" width="100%" {...containerProps}>
            {header}
            {listbox}
            {footer}
          </Flexbox>
        </StyledPopoverContent>
      )}
    </FloatingPortal>
  );
});
BaseComboboxContent.className = CLASSNAME;
BaseComboboxContent.displayName = COMPONENT_NAME;

export const ComboboxContent = Object.assign(BaseComboboxContent, {
  Header: ComboboxContentHeader,
  Listbox: ComboboxContentListbox,
  Footer: ComboboxContentFooter,
});
